"use client";

import { GameCard } from "@/components/feature/game-card";
import { PointerInteractionAutoProvider } from "@/components/feature/pointer-interaction-media";
import { PreviewIntentAutoProvider } from "@/components/feature/preview-intent";
import { RiveTile } from "@/components/feature/rive-tile";
import { usePinterestGridCentering } from "@/hooks/use-pinterest-grid-centering";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { sumGridColumnSpans } from "@/lib/showcase-grid/centered-columns";
import {
  CATALOG_GRID_MOBILE_MEDIA,
  clampShowcaseGridDimensions,
  legacySizeFromDimensions,
} from "@/lib/showcase-grid/dimensions";
import type { GameSize } from "@/types/game";
import type { ShowcaseGridDisplayItem, ShowcaseGridSpan } from "@/types/showcase-grid";
import { showcaseGridItemStyle } from "@/types/showcase-grid";
import { useMemo } from "react";
import "@/components/feature/masonry.css";

interface ShowcaseGridProps {
  items: ShowcaseGridDisplayItem[];
  emptyMessage?: string;
  emptyDescription?: string;
  pageOrigin?: PageOriginContext | null;
  linkPrefetch?: boolean;
  /**
   * When true, shrinks the declared column count to the content span and centers
   * leftover horizontal space. Tile pixel size still matches a full-width grid.
   * Off by default so the games hub / catalog stay left-packed.
   */
  centerWhenSparse?: boolean;
  /**
   * Grid and curved row: columns of side inset on a 6-column frame.
   * 0.5 removes one column total. Max 2 per side.
   */
  sidePadding?: number;
}

const uiSizeMap: Record<ShowcaseGridSpan, "small" | "medium" | "large"> = {
  1: "small",
  2: "medium",
  3: "large",
};

/**
 * Public-facing grid that renders mixed showcase elements (games, images, Rive
 * files) using the shared Pinterest-style masonry layout.
 */
export function ShowcaseGrid({
  items,
  emptyMessage = "No Games Available",
  emptyDescription = "Check back soon for new games!",
  pageOrigin,
  linkPrefetch = true,
  centerWhenSparse = false,
  sidePadding = 0,
}: ShowcaseGridProps) {
  const isMobileGrid = useMediaQuery(CATALOG_GRID_MOBILE_MEDIA);
  const contentColumnSpan = useMemo(
    () =>
      sumGridColumnSpans(
        items.map((item) => {
          const dims = isMobileGrid
            ? clampShowcaseGridDimensions({ cols: item.gridCols, rows: item.gridRows })
            : { cols: item.gridCols, rows: item.gridRows };
          return dims.cols;
        })
      ),
    [items, isMobileGrid]
  );
  const gridRef = usePinterestGridCentering(contentColumnSpan, centerWhenSparse, sidePadding);

  if (items.length === 0) {
    return (
      <div className="bg-white/10 p-8 rounded-2xl text-center">
        <h2 className="text-xl font-bold mb-2">{emptyMessage}</h2>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <PreviewIntentAutoProvider>
      <PointerInteractionAutoProvider>
        <div ref={gridRef} className="games-pinterest-grid" data-testid="showcase-pinterest-grid">
          {items.map((item) => {
            const dims = isMobileGrid
              ? clampShowcaseGridDimensions({ cols: item.gridCols, rows: item.gridRows })
              : { cols: item.gridCols, rows: item.gridRows };
            const legacySize = legacySizeFromDimensions(dims);

            return (
              <div
                key={item.id}
                className="grid-item"
                style={showcaseGridItemStyle({ cols: item.gridCols, rows: item.gridRows })}
              >
                {item.type === "game" ? (
                  <GameCard
                    game={{
                      ...item.game,
                      size: legacySize as unknown as GameSize,
                    }}
                    size={uiSizeMap[legacySize]}
                    pageOrigin={pageOrigin}
                    linkPrefetch={linkPrefetch}
                    previewIdOverride={item.id}
                  />
                ) : item.type === "rive" ? (
                  <RiveTile
                    src={item.assetUrl}
                    className="h-full w-full"
                    ariaLabel="Decorative animation"
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={item.assetUrl}
                    alt=""
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                )}
              </div>
            );
          })}
        </div>
      </PointerInteractionAutoProvider>
    </PreviewIntentAutoProvider>
  );
}
